"use client";
import React, { useMemo, useState, useEffect } from "react";
import type { Template, TemplateNode, MenuData, Item, Category } from "../components/types/template";
import type { TemplateThemeMode } from "../components/types/template";
import {
  resolvePath,
  resolveAllBindings,
  splitColumns,
  injectBindings,
  findAllRepeaters,
  computeStyle,
  applyTextUtils,
  useResponsiveScale,
  resolveBinding,
  resolveClassName,
  resolveClassNameBindings,
  resolveTemplateThemeMode,
  // resolveTemplateTokenString,
  // resolveTemplateVariables,
  resolveRepeaterCollection,
  inferRepeaterContextKey, 
  extendRepeaterContext,
  isBindingMap,
  pickRenderableText,
  renderTextOrChildren,
  resolveRenderableValue,
} from "../components/lib/util";
import DefaultModal from "../components/data/Modals";
import { resolveBindingExpression } from "../components/lib/util";
import clsx from "clsx"; 
import { validateLegacyTemplateSchema } from "../components/lib/templateValidator";
import { Moon, Sun } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import {
  resolveTemplateVariables,      // replaces the old util.ts import of same name
  resolveCanonicalVariables,     // NEW , use in paged-flip palette builder
  buildTemplateCssVars,          // NEW , CSS var injection
  buildTemplateThemeContextValue,// NEW , context value builder
  resolveTemplateTokenString,    // replaces the old util.ts import of same name
} from "../components/lib/templateVariables";
import { TemplateThemeContext } from "../components/lib/templateThemeContext";
import {
  resolveImagePresentation,
  resolveCutoutSource,
  resolveDecorAssetUrl,
  resolveImageFallbackInitial,
  resolveImageFallbackPresentation,
} from "../components/lib/templateImage";
import { resolveGroupLayout } from "../components/lib/templateLayout";



const TEMPLATE_THEME_SURFACE_VARS: Record<TemplateThemeMode, React.CSSProperties> = {
  light: {
    "--background": "#ffffff",
    "--foreground": "#0f0f0f",
    "--card": "#ffffff",
    "--card-foreground": "#0f0f0f",
    "--popover": "#ffffff",
    "--popover-foreground": "#0f0f0f",
    "--primary": "#0f0f0f",
    "--primary-foreground": "#ffffff",
    "--secondary": "#f5f5f5",
    "--secondary-foreground": "#161616",
    "--muted": "#f5f5f5",
    "--muted-foreground": "#555555",
    "--accent": "#ff5a3c",
    "--accent-foreground": "#ffffff",
    "--destructive": "#d73939",
    "--destructive-foreground": "#ffffff",
    "--border": "rgba(15, 15, 15, 0.12)",
    "--input-background": "#ffffff",
  } as React.CSSProperties,
  dark: {
    "--background": "#0f0f0f",
    "--foreground": "#f5f5f5",
    "--card": "#171717",
    "--card-foreground": "#f5f5f5",
    "--popover": "#171717",
    "--popover-foreground": "#f5f5f5",
    "--primary": "#f5f5f5",
    "--primary-foreground": "#0f0f0f",
    "--secondary": "#1f1f1f",
    "--secondary-foreground": "#f5f5f5",
    "--muted": "#1a1a1a",
    "--muted-foreground": "#a3a3a3",
    "--accent": "#ff5a3c",
    "--accent-foreground": "#ffffff",
    "--destructive": "#ef4444",
    "--destructive-foreground": "#ffffff",
    "--border": "rgba(245, 245, 245, 0.12)",
    "--input-background": "#1a1a1a",
  } as React.CSSProperties,
};

// Recursively render modal nodes with advanced binding support
function renderModalNode(
  node: TemplateNode,
  context: Record<string, unknown>,
  { openModal, scales, scaleTexts, isCompactSplitLayout, sliderState, setSliderState, sliderConfig }: {
    openModal: (id: string | null) => void;
    scales: { font: number; spacing: number; layout: number };
    scaleTexts: boolean;
    isCompactSplitLayout?: boolean;
    sliderState?: Record<string, number>;
    setSliderState?: (s: Record<string, number>) => void;
    sliderConfig?: {
      enabled: boolean;
      mainDivId?: string;
      leftBtnId?: string;
      rightBtnId?: string;
      itemsPerPage?: number;
    };
  }
): React.ReactNode {


  // Handle conditional rendering
  if (node.props?.ifPromo && !context.promo) return null;
  if (
    node.props?.ifPromoType &&
    (
      !context.promo ||
      !(
        Array.isArray(node.props.ifPromoType)
          ? node.props.ifPromoType
          : [node.props.ifPromoType]
      ).includes((context.promo as Record<string, any>)?.discount_type)
    )
  ) return null;
  if (node.props?.show === false) return null;

  // Special: close button
  let handleClick: (() => void) | undefined;
  if (node.type === "button" && node.props?.onClick === true) {
    handleClick = () => openModal(null);
  }

  // Resolve all bindings, including advanced expressions
  const resolvedProps: Record<string, unknown> = {};
  // Support dynamic show/hide via binding expression
  if (typeof node.props?.show === "string") {
    const showResult = resolveBindingExpression(node.props.show, context);
    if (!showResult || showResult === "false") return null;
  }

  const modalBindings = isBindingMap(node.props?.bindings) ? node.props.bindings : {};
  if (Object.keys(modalBindings).length > 0) {
    for (const key in modalBindings) {
      const bindingValue = modalBindings[key];
      // Only process if bindingValue is a string or a non-empty object with expected properties
      if (key === 'field' && typeof bindingValue === "string") {
        resolvedProps[key] = resolveBinding({ field: bindingValue }, context);
      } else if (typeof bindingValue === "string") {
        resolvedProps[key] = resolveBinding(bindingValue, context);
      }
    }
  }

  Object.keys(resolvedProps).forEach((key) => {
    resolvedProps[key] = resolveRenderableValue(resolvedProps[key], context);
  });

  // Merge style and promo styles
  const { style, className: modalClassName } = computeStyle(node, scales, scaleTexts, context, true);

  const animationClass = node.props?.animate ? `menuraq-anim-${node.props.animate}` : undefined;
  const anim = [ animationClass].filter(Boolean).join(" ") || undefined

  
  // --- PREPARE ELEMENT PROPS ---
  const elementProps: Record<string, unknown> = {
    key: node.id,
    id: node.props?.id,
    ...(node.props || {}),
  };
  elementProps.className = clsx(
    resolveClassName(`${node.props?.className || ""} ${anim || ""}`, context),
    modalClassName,
  );


  [
    "repeater", "modal", "show", "animate", "onClick", "responsiveFont", "responsiveSpacing",
    "truncate", "ellipsis", "utils", "targetId", "bindings", "structureId", "layout",
    "hotspot", "showInSlider", "imageBinding", "sliderNav", "sliderMain", "column",
    "promoStyle", "promoTypeStyles", "ifPromo", "ifPromoType", "pageSlot"
  ].forEach(k => delete elementProps[k]);


  // Recursively render children
  let children: React.ReactNode = null;
  if (node.children && node.children.length > 0) {
    children = node.children.map(child =>
      renderModalNode(child, context, { openModal, scales, scaleTexts, sliderState, setSliderState, sliderConfig })
    );
  }


  // Text content
  const text = applyTextUtils(
    pickRenderableText(
      resolveRenderableValue(resolvedProps.text, context),
      resolveRenderableValue(resolvedProps.field, context),
      resolveRenderableValue(node.props?.text, context),
      resolveRenderableValue(node.props?.children, context),
    ),
    node
  );
    
  // Render element
  switch (node.type) {
    case "img":
      return (
        <img
          key={node.id}
          style={style}
          {...elementProps}
          src={resolveRenderableValue(resolvedProps.src ?? node.props?.src, context) as string | undefined}
          alt={resolveRenderableValue(resolvedProps.alt ?? node.props?.alt, context) as string | undefined}
        />
      );
    case "button":
      return (
        <button
          key={node.id}
          style={style}
          {...elementProps}
          onClick={handleClick}
        >
          {renderTextOrChildren(text, children)}
        </button>
      );
    case "div":
    case "span":
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
    case "p":
    case "label":
      return React.createElement(node.type, { key: node.id, style, onClick: handleClick, ...elementProps }, renderTextOrChildren(text, children));
    default:
      return React.createElement(node.type, { key: node.id, style, onClick: handleClick, ...elementProps }, renderTextOrChildren(text, children));
  }
}

// Main modal overlay renderer
export function ModalOverlay({
  modalNode,
  context,
  openModal,
  scales,
  scaleTexts,
  isCompactSplitLayout,
  sliderState,
  setSliderState,
  sliderConfig
}: {
  modalNode: TemplateNode;
  context: Record<string, unknown>;
  openModal: (id: string | null) => void;
  scales: { font: number; spacing: number; layout: number };
  scaleTexts: boolean;
  isCompactSplitLayout?: boolean;
  sliderState?: Record<string, number>;
  setSliderState?: (s: Record<string, number>) => void;
  sliderConfig?: {
    enabled: boolean;
    mainDivId?: string;
    leftBtnId?: string;
    rightBtnId?: string;
    itemsPerPage?: number;
  };
}) {
  // Overlay click closes modal
  const handleOverlayClick = () => openModal(null);
  const { style, className: cls } = computeStyle(modalNode, scales, scaleTexts, context, true);
  return (
    <div
      style={{
        position: "fixed",
        zIndex: 1001,
        pointerEvents: "all",
        left: 0,
        top: 0,
        width: "100vw",
        height: "100vh",
        background: "var(--mnrq-shell-overlay, rgba(0,0,0,0.4))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
      onClick={handleOverlayClick}
    >
      <div
        style={style}
        className={cls + " " + modalNode.props?.className}
        onClick={e => e.stopPropagation()}
      >
        {renderModalNode(modalNode, context, { openModal, scales, scaleTexts, isCompactSplitLayout, sliderState, setSliderState, sliderConfig })}
      </div>
    </div>
  );
}



// --- SLIDER RENDERER (for slider templates only) ---
function renderSlider(
  node: TemplateNode,
  context: Record<string, unknown>,
  splitItemsColumns: boolean,
  {
    openModal,
    scales,
    scaleTexts,
    isCompactSplitLayout,
    sliderState,
    setSliderState,
    sliderConfig,
  }: { 
    openModal: (id: string | null) => void;
    scales: { font: number; spacing: number; layout: number; dense?: boolean; phone?: boolean; phoneColumns?: 1 | 2 };
    scaleTexts: boolean;
    isCompactSplitLayout?: boolean;
    sliderState?: Record<string, number>;
    setSliderState?: (s: Record<string, number>) => void;
    sliderConfig?: {
      enabled: boolean;
      mainDivId?: string;
      leftBtnId?: string;
      rightBtnId?: string;
      itemsPerPage?: number;
    };
  } = {
    openModal: () => {},
    scales: { font: 1, spacing: 1, layout: 1 },
    scaleTexts: true,
    isCompactSplitLayout: false,
  }
): React.ReactNode {
  // renderSlider is only called when the node has splitItemsColumns or
  // splitCategoriesColumns, so we always split. Previously this checked
  // context.template?.splitColumns (a template-level flag) which could be
  // unset, causing both column repeaters to render the full unsplit data.
  const leftRepeater = findAllRepeaters(node).find(r => r.props?.column === 0);
  const rightRepeater = findAllRepeaters(node).find(r => r.props?.column === 1);

  if (!leftRepeater || !rightRepeater) {
    // Graceful fallback: if the template node doesn't define column=0/1
    // repeaters, render children normally instead of showing nothing.
    return (
      <div key={node.id} id={node.props?.id}>
        {node.children?.map(child =>
          renderAndExpand(child, context, { openModal, scales, scaleTexts, isCompactSplitLayout, sliderState, setSliderState, sliderConfig })
        )}
      </div>
    );
  }

  const data = splitItemsColumns
    ? (context.category as Record<string, any>)?.items || (context.menu as Record<string, any>)?.items || []
    : (context.menu as Record<string, any>)?.categories || [];
  const [leftData, rightData] = data.length > 0 ? splitColumns(data) : [[], []];

  const { style, className: cls } = computeStyle(node, scales, scaleTexts, context, true);
  const splitGap = isCompactSplitLayout ? 12 : 32;

  return (
    <div
      key={node.id}
      id={node.props?.id}
      className={cls + " " + node.props?.className}
      style={style}
    >
      <div
        style={{
          display: "flex",
           flexDirection: "row",
           gap: splitGap,
           alignItems: "flex-start",
           overflowX: "hidden",
           overflowY: "hidden",
        }}
      >
        {/* Left Column */}
        <div key="left-col" style={{ ...(leftRepeater.props?.style || {}), flex: 1, minWidth: 0, overflow: isCompactSplitLayout ? "hidden" : undefined }}>
          {renderAndExpand(
            leftRepeater,
            splitItemsColumns
              ? { ...context, items: leftData, category: { ...(context.category as Record<string, any> || {}), items: leftData } }
              : { ...context, categories: leftData, menu: { ...(context.menu as Record<string, any> || {}), categories: leftData } },
            { openModal, scales, scaleTexts, isCompactSplitLayout, sliderState, setSliderState, sliderConfig }
          )}
        </div>
        {/* Right Column */}
        <div key="right-col" style={{ ...(rightRepeater.props?.style || {}), flex: 1, minWidth: 0, overflow: isCompactSplitLayout ? "hidden" : undefined }}>
          {renderAndExpand(
            rightRepeater,
            splitItemsColumns
              ? { ...context, items: rightData, category: { ...(context.category as Record<string, any> || {}), items: rightData } }
              : { ...context, categories: rightData, menu: { ...(context.menu as Record<string, any> || {}), categories: rightData } },
            { openModal, scales, scaleTexts, isCompactSplitLayout, sliderState, setSliderState, sliderConfig }
          )}
        </div>
      </div>
    </div>
  );
}

// --- Main recursive renderer (default, non-slider) ---
function renderAndExpand(
  node: TemplateNode,
  context: Record<string, unknown>,
  {
    openModal,
    scales,
    scaleTexts,
    isCompactSplitLayout,
    sliderState,
    setSliderState,
    sliderConfig,
  }: {
    openModal: (id: string | null) => void;
    scales: { font: number; spacing: number; layout: number; dense?: boolean; phone?: boolean; phoneColumns?: 1 | 2 };
    scaleTexts: boolean;
    isCompactSplitLayout?: boolean;
    sliderState?: Record<string, number>;
    setSliderState?: (s: Record<string, number>) => void;
    sliderConfig?: {
      enabled: boolean;
      mainDivId?: string;
      leftBtnId?: string;
      rightBtnId?: string;
      itemsPerPage?: number;
    };
  } = {
    openModal: () => {},
    scales: { font: 1, spacing: 1, layout: 1 },
    scaleTexts: true,
    isCompactSplitLayout: false,
  }
): React.ReactNode {
  // --- PROMO LOGIC: Find nearest promo in context ---
  const promo =
    (context?.item as Record<string, any>)?.promotion ||
    (context?.category as Record<string, any>)?.promotion ||
    context?.promo ||
    context?.parentPromo;

  // --- IF PROMO LOGIC ---
  if (node.props?.ifPromo) {
    if (!promo) return null;
  }
  // --- IF PROMO TYPE LOGIC ---
  if (node.props?.ifPromoType) {
    if (!promo) return null;
    const types = Array.isArray(node.props.ifPromoType)
      ? node.props.ifPromoType
      : [node.props.ifPromoType];
    if (!types.includes((promo as Record<string, any>)?.discount_type)) return null;
  }

  const currentItem = (context?.item ?? {}) as Record<string, any>;
  const hasAvailabilityData = Boolean(
    currentItem?.availability_label ||
      (Array.isArray(currentItem?.available_Days) && currentItem.available_Days.length > 0) ||
      (currentItem?.available_Times?.start || currentItem?.available_Times?.end),
  );
  if (node.props?.availabilityOnly && !hasAvailabilityData) return null;
  if (node.props?.hideWhenUnavailable && currentItem?.available === false) return null;

  const splitItemsColumns = node.props?.splitItemsColumns || false;
  const splitCategoriesColumns = node.props?.splitCategoriesColumns || false;

  // --- SLIDER ENTRYPOINT ---
  if (
    splitItemsColumns || splitCategoriesColumns
  ) {
    return renderSlider(node, context, splitItemsColumns, {
      openModal,
      scales,
      scaleTexts,
      isCompactSplitLayout,
      sliderState,
      setSliderState,
      sliderConfig,
    });
  }
  // --- REPEATER LOGIC (non-slider, default scroll) ---
  //
  // A repeater node renders its OWN element once, wrapping the expanded cells.
  // The wrapper carries the node's `style` (grid `repeat(n, …)`, flex gaps) and
  // its `layout` class (`.menuraq-scatter8`, `.menuraq-split-alt`, …): returning
  // the children bare dropped both, which is why every catalog grid and
  // `.menuraq-* > *` selector was invisible. Do NOT return the cells directly.
  let repeaterChildren: React.ReactNode = null;
  if (node.props?.repeater) {
    const repeaterData = resolveRepeaterCollection(node, context);
    const contextKey = inferRepeaterContextKey(node.props?.bindings?.repeatFor);

    const expanded = repeaterData
      .map((entry: unknown, idx: number) => {
        return (node.children || []).map((child) => {
          const e = entry as Record<string, any>;
          const repeatedChild = {
            ...child,
            id: `${node.id}__${idx}__${child.id}`,
            props: {
              ...(child.props || {}),
              id: `${node.id}__${idx}__${child.id}`,
              'data-repeat-root': 'true',
              'data-repeat-context': contextKey,
              'data-repeat-index': String(idx),
              ...(contextKey === 'item'
                ? {
                    'data-item-id': String(e?.id || ''),
                  }
                : {}),
              ...(contextKey === 'category'
                ? {
                    'data-category-id': String(e?.sourceCategoryId || e?.id || ''),
                    'data-category-continuation': e?.continuation ? 'true' : 'false',
                  }
                : {}),
            } as Record<string, unknown>,
          };

          const injected =
            contextKey === "category" ||
            contextKey === "collection" ||
            contextKey === "item" ||
            contextKey === "section" ||
            contextKey === "page" ||
            contextKey === "metaRow" ||
            contextKey === "tocEntry"
              ? injectBindings(repeatedChild, e, contextKey, idx)
              : repeatedChild;

          const nextContext = extendRepeaterContext(
            { ...context, promo, parentPromo: promo },
            contextKey,
            entry,
          );

          return renderAndExpand(injected, nextContext, {
            openModal,
            scales,
            scaleTexts,
            isCompactSplitLayout,
            sliderState,
            setSliderState,
            sliderConfig,
          });
        });
      })
      .flat();

    if (expanded.length === 0) return null;
    repeaterChildren = expanded;
  }

  // --- MODAL HOTSPOT LOGIC ---
  let handleClick: (() => void) | undefined;
  const contextItem = context.item as Record<string, any> | undefined;
  const contextCategory = context.category as Record<string, any> | undefined;
  if (node.props?.hotspot === "modal" && openModal) {
    if (contextItem?.id) {
      handleClick = () => openModal(contextItem.id);
    } else if (contextCategory?.id) {
      handleClick = () => openModal(contextCategory.id);
    }
  } else if (node.props?.modal && openModal) {
    if (contextItem?.id) {
      handleClick = () => openModal(contextItem.id);
    } else if (contextCategory?.id) {
      handleClick = () => openModal(contextCategory.id);
    }
  } else if (node.props?.modal){
        if (node.props.onClick) {
          handleClick = () => {
            openModal(null);
          };
      }
    }

  // --- BINDINGS ---
  const bindingMap = isBindingMap(node.props?.bindings) ? node.props.bindings : {};
  const resolvedProps = resolveAllBindings(bindingMap, {
    ...context,
    promo, // expose promo for bindings 
    promotion: promo, // alias for convenience
  }) as Record<string, any>;

  Object.keys(resolvedProps).forEach((key) => {
    resolvedProps[key] = resolveRenderableValue(resolvedProps[key], context);
  });

  // --- IMAGE BINDING LOGIC ---
  let imgSrc = (resolvedProps.src ?? node.props?.src) as string | undefined;
  let imageShapeOverride: string | undefined;
  if (node.type === "img") {
    if (node.props?.imageBinding) {
      imgSrc = (resolvePath(context, node.props.imageBinding) as string | undefined) || node.props.src;
    }
    const entry = (context.item ?? context.category) as Record<string, unknown> | undefined;
    const cut = resolveCutoutSource(node.props?.cutout, entry, imgSrc);
    imgSrc = cut.src;
    imageShapeOverride = cut.shapeOverride;
    if (node.props?.decor && typeof imgSrc === "string") {
      imgSrc = resolveDecorAssetUrl(imgSrc) ?? imgSrc;
    }
  }
  const imagePresentation = node.type === "img"
    ? resolveImagePresentation({
        shape: imageShapeOverride ?? node.props?.shape,
        ring: node.props?.ring,
        shadow: node.props?.shadow,
      })
    : undefined;

  const animationClass = node.props?.animate ? `menuraq-anim-${node.props.animate}` : undefined;
  
  const anim = [ animationClass].filter(Boolean).join(" ") || undefined;
  
  // --- PREPARE ELEMENT PROPS ---
  const elementProps: Record<string, unknown> = {
    key: node.id,
    id: node.props?.id,
    ...(node.props || {}),
  };

  const cl = resolveClassName(
    resolveClassNameBindings(`${node.props?.className || ""} ${anim || ""}`, context),
    context,
  );
  [
    "repeater", "modal", "show", "animate", "onClick", "responsiveFont", "responsiveSpacing",
    "truncate", "ellipsis", "utils", "targetId", "bindings", "structureId", "layout",
    "hotspot", "showInSlider", "imageBinding", "sliderNav", "sliderMain", "column",
    "promoStyle", "promoTypeStyles", "ifPromo", "ifPromoType", "splitItemsColumns", "splitCategoriesColumns",
    "availabilityOnly", "hideWhenUnavailable", "featuredOnly", "sectionId", "sectionIdBinding", "categoryIds", "categoryIdsBinding", "pageSlot",
    "shape", "ring", "shadow", "cutout", "decor"
  ].forEach(k => delete elementProps[k]);

  const { className, style } = computeStyle(node, scales, scaleTexts ?? false, { ...context, parentPromo: promo }, true);

  let cls = cl + (className ? ` ${className} ` : "");

  const groupLayout = node.props?.layout
    ? resolveGroupLayout(node.props.layout, { phone: Boolean(scales.phone), phoneColumns: scales.phoneColumns })
    : undefined;
  if (groupLayout) {
    if (groupLayout.className) cls = `${cls} ${groupLayout.className}`.trim();
    if (groupLayout.style) Object.assign(style, groupLayout.style);
  }
   // --- TEXT CONTENT ---
  const text = applyTextUtils(
    pickRenderableText(
      resolveRenderableValue(resolvedProps.text, context),
      resolveRenderableValue(resolvedProps.children, context),
      resolveRenderableValue(node.props?.text, context),
      resolveRenderableValue(node.props?.children, context),
    ),
    node
  );

  if (node.type === "img") {
    [
     "src", "key", "alt"
    ].forEach(k => delete elementProps[k])
  }

  // --- CHILDREN ---
  let children: React.ReactNode = null;
  if (repeaterChildren) {
    // Repeater nodes render their expanded cells inside their own element.
    children = repeaterChildren;
  } else if (node.children && node.children.length > 0) {
    children = node.children.map(child =>
      renderAndExpand(
        child,
        { ...context, parentPromo: promo, promo },
        { openModal, scales, scaleTexts, isCompactSplitLayout, sliderState, setSliderState, sliderConfig },
      )
    );
  }

  // --- RENDER ELEMENT ---
  switch (node.type) {
    case "img":
      if (!imgSrc) {
        // No photo: draw the accent monogram disc (doc 03 §2) instead of a grey
        // box or nothing. Decor nodes simply drop out.
        if (node.props?.decor) return null;
        const fallbackEntry = (context.item ?? context.category) as Record<string, unknown> | undefined;
        const fallback = resolveImageFallbackPresentation(imageShapeOverride ?? node.props?.shape);
        return (
          <div
            {...elementProps}
            key={node.id}
            className={clsx(cls, imagePresentation?.className)}
            style={{ ...style, ...imagePresentation?.style, ...fallback.style, fontSize: style.fontSize ?? "1.5rem" }}
            aria-hidden={true}
          >
            {resolveImageFallbackInitial(fallbackEntry)}
          </div>
        );
      }
      return (
        <div
          {...elementProps}
          key={node.id}
          className={`h-[${style.height}] w-[${style.width}]`}
          aria-hidden={node.props?.decor ? true : undefined}
          style={node.props?.decor ? { pointerEvents: "none" } : undefined}
        >
          <Image
            className={clsx(cls, imagePresentation?.className)}
            style={{...style, ...imagePresentation?.style}}
            width={Number(style.width) || 100}
            height={Number(style.height) || 100}
            draggable={!node.props?.decor}
            alt={node.props?.decor ? "" : (resolveRenderableValue(resolvedProps.alt ?? node.props?.alt, context) as string | undefined) || ""}

            src={(resolveRenderableValue(imgSrc, context) as string | undefined) || ""}
          />
        </div>
      );
    case "button":
      return (
        <button {...elementProps} className={clsx(cls)} style={{...style}} disabled={node.props?.disabled} onClick={handleClick}>
          {renderTextOrChildren(text, children)}
        </button>
      );
    case "a":
      return (
        <Link
          {...elementProps}
          className={clsx(cls)}
          style={{...style}}
          href={resolveRenderableValue(resolvedProps.href ?? node.props?.href, context) as string}
          target={resolveRenderableValue(resolvedProps.target ?? node.props?.target, context) as string | undefined}
          onClick={handleClick}
        >
          {renderTextOrChildren(text, children)}
        </Link>
      );
    case "input":
      return <input {...elementProps} className={clsx(cls)} style={{...style}} value={resolvedProps.value ?? node.props?.value} />;
    case "textarea":
      return <textarea {...elementProps} className={clsx(cls)} style={{...style}} value={resolvedProps.value ?? node.props?.value} />;
    case "select":
      return <select {...elementProps} className={clsx(cls)} style={{...style}} onClick={handleClick}>{children}</select>;
    case "option":
      return <option {...elementProps} className={clsx(cls)} style={{...style}} onClick={handleClick}>{renderTextOrChildren(text, children)}</option>;
    case "ul":
    case "ol":
      return React.createElement(node.type,  {...elementProps, className: clsx(cls), style: {...style}, onClick: handleClick}, children);
    case "li":
      return <li {...elementProps} className={clsx(cls)} style={{...style}} onClick={handleClick}>{renderTextOrChildren(text, children)}</li>;
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
    case "p":
    case "span":
    case "label":
    case "figcaption":
      return React.createElement(node.type, {...elementProps, className: clsx(cls), style: {...style}, onClick: handleClick}, renderTextOrChildren(text, children));
    case "table":
    case "thead":
    case "tbody":
    case "tr":
    case "td":
    case "th":
      return React.createElement(node.type, {...elementProps, className: clsx(cls), style: {...style}, onClick: handleClick}, children);
    case "video":
      return <video {...elementProps} className={clsx(cls)} style={{...style}} src={resolveRenderableValue(resolvedProps.src ?? node.props?.src, context) as string | undefined} onClick={handleClick}>{children}</video>;
    case "audio":
      return <audio {...elementProps} className={clsx(cls)} style={{...style}} src={resolveRenderableValue(resolvedProps.src ?? node.props?.src, context) as string | undefined} onClick={handleClick}>{children}</audio>;
    case "iframe":
      return <iframe {...elementProps} className={clsx(cls)} style={{...style}} src={resolveRenderableValue(resolvedProps.src ?? node.props?.src, context) as string | undefined} onClick={handleClick}/>;
    // ...inside renderAndExpand, under case "modal":
    case "modal": {
      return null;
    }
    default:
      return React.createElement(node.type, { ...elementProps, className: clsx(cls), style: { ...style }, onClick: handleClick }, children);
  }
}

// --- Main TemplateRenderer Component ---

export function TemplateRenderer({
  template,
  data,
  showThemeToggle = false,
  themeTogglePosition = "bottom-right",
  forcedThemeMode,
  onThemeModeChange: _onThemeModeChange,
}: {
  template: Template;
  data: MenuData;
  showThemeToggle?: boolean;
  themeTogglePosition?: "bottom-right" | "top-right";
  forcedThemeMode?: TemplateThemeMode;
  onThemeModeChange?: (mode: TemplateThemeMode) => void;
}) {
  
  if (!data) {
    return null
  }
  if (!template) {
    return null
  }

  const validation = useMemo(() => validateLegacyTemplateSchema(template), [template]);
  const [themeOverride, setThemeOverride] = useState<TemplateThemeMode | null>(null);

  const hasDualThemeVariants = Boolean(
    template.themeVariants?.light &&
    template.themeVariants?.dark
  );

  const baseThemeMode = forcedThemeMode || resolveTemplateThemeMode(template, false);
  const activeThemeMode = themeOverride || baseThemeMode;
  const templateResolvedVariables = useMemo(
    () => resolveTemplateVariables(template, activeThemeMode),
    [template, activeThemeMode]
  );

  useEffect(() => {
    setThemeOverride(null);
  }, [forcedThemeMode]);

  const { ref, scales, isCompactSplitLayout } = useResponsiveScale(template);
  const [openModalId, setOpenModalId] = useState<string | null>(null);

  // Slider state: { [mainDivId]: currentPage }
  const [sliderState, setSliderState] = useState<Record<string, number>>({});

  // Use template.scaleTexts (default true)
  const scaleTexts = template.scaleTexts !== false;

  // Slider config (if enabled)
  const sliderConfig = template.slider
    ? {
        enabled: true,
        mainDivId: template.sliderProps?.mainDivId,
        leftBtnId: template.sliderProps?.leftBtnId,
        rightBtnId: template.sliderProps?.rightBtnId,
        itemsPerPage: template.sliderProps?.itemsPerPage,
      }
    : { enabled: false };

  // Pass template + resolved variables in context for splitColumns, theme modes, etc.

  const contextWithTemplate = useMemo(
    () => ({
      ...data,
      template: {
        ...template,
        variables: templateResolvedVariables,
      },
      templateResolvedVariables,
      templateConfig: template.templateConfig || {},
      themeMode: activeThemeMode,
      // Doc 03 §2/§5 — expose the active seasonal skin so templates can opt in
      // with `show: '{skin}'` / `${headline}` (written by applyDesignOverrides).
      skin: templateResolvedVariables.skin
        ? {
            id: templateResolvedVariables.skin,
            headline: templateResolvedVariables.headline || '',
            decor: templateResolvedVariables.skinDecor || '',
          }
        : undefined,
    }),
    [data, template, templateResolvedVariables, activeThemeMode],
  );

  // Support both array and single object for modals
  const modalNodes = Array.isArray(template.modals)
    ? template.modals
    : template.modals
      ? [template.modals]
      : [];

  const itemModalNode = modalNodes.find(node => node.type === "modal" && node.id === "item-modal");

  const hasModal = template.hasModal;

  // ...inside TemplateRenderer...
  const renderModal = useMemo(() => {
    if (!itemModalNode || !openModalId) return null;
    let foundItem = null;
    let foundCategory = null;
    for (const cat of data.menu?.categories || []) {
      const found = (cat.items || []).find((it: Item) => it.id === openModalId);
      if (found) {
        foundItem = found;
        foundCategory = cat;
        break;
      }
    }
    return (
      <ModalOverlay
        modalNode={itemModalNode}
        context={{ ...contextWithTemplate, item: foundItem, category: foundCategory, promo: foundItem?.promotion || foundCategory?.promotion }}
        openModal={setOpenModalId}
        scales={scales}
        scaleTexts={scaleTexts}
        isCompactSplitLayout={isCompactSplitLayout}
        sliderState={sliderState}
        setSliderState={setSliderState}
        sliderConfig={sliderConfig}
      />
    );
  }, [itemModalNode, openModalId, contextWithTemplate, scales, sliderState, isCompactSplitLayout]);

  const themeContextValue = useMemo(
    () => buildTemplateThemeContextValue(templateResolvedVariables, activeThemeMode),
    [templateResolvedVariables, activeThemeMode]
  );

  // Render and expand all nodes in one pass
  const renderedNodes = useMemo(
    () =>
      template.nodes?.map(node =>
        renderAndExpand(
          node,
          contextWithTemplate,
          {
            openModal: setOpenModalId,
            scales,
            scaleTexts,
            isCompactSplitLayout,
            sliderState,
            setSliderState,
            sliderConfig,
          }
        )
      ),
    [template, contextWithTemplate, scales, sliderState, isCompactSplitLayout]
  );

  // --- Modal logic ---
  // Find the item by openModalId
  // Find modalItem and modalCategory for the open modal
  const [modalItem, setModalItem] = useState<Record<string, unknown> | null>(null);
  const [modalCategory, setModalCategory] = useState<Record<string, unknown> | null>(null);
 
  useEffect(() => {
    if (!openModalId) {
      setModalItem(null);
      setModalCategory(null);
      return;
    }
    let foundItem = null;
    let foundCategory = null;
    for (const cat of data.menu?.categories || []) {
      const found = (cat.items || []).find((it: Item) => it.id === openModalId);
      if (found) {
        foundItem = found;
        foundCategory = cat;
        break;
      }
    }
    setModalItem(foundItem as Record<string, unknown> | null);
    setModalCategory(foundCategory as Record<string, unknown> | null);
  }, [openModalId, data.menu?.categories]);

  const resolvedCanvasBackground =
    // Theme gradient layer wins over the flat canvas value; the root div uses
    // the `background` shorthand, which accepts gradient strings directly.
    templateResolvedVariables.colors.backgroundGradient ||
    resolveTemplateTokenString(
      template.canvas?.background?.value || "#fff",
      templateResolvedVariables,
    );

  const canRenderThemeToggle =
    showThemeToggle &&
    hasDualThemeVariants &&
    template.themeBehavior !== "fixed-dark" &&
    template.themeBehavior !== "fixed-light";
  const themeTogglePositionClasses =
    themeTogglePosition === "top-right"
      ? "fixed right-3 top-3 sm:right-4 sm:top-4"
      : "fixed bottom-3 right-3 sm:bottom-4 sm:right-4";

  if (!validation.valid) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-semibold">Template validation failed.</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {validation.issues
            .filter((issue) => issue.level === "error")
            .map((issue, index) => (
              <li key={`${issue.nodeId || "template"}-${index}`}>
                {issue.nodeId ? `[${issue.nodeId}] ` : ""}
                {issue.message}
              </li>
            ))}
        </ul>
      </div>
    );
  }

  // Check if template defines its own modal nod

  return (
    <TemplateThemeContext.Provider value={themeContextValue}>
      <div
        ref={ref}
        className={clsx(scales.phone && "menuraq-phone")}
        style={{
          width: "100%",
          minHeight: "100vh",
          background: resolvedCanvasBackground || "#fff",
          position: "relative",
          overflow: "auto",
          padding: 0,
          colorScheme: activeThemeMode,
          ...TEMPLATE_THEME_SURFACE_VARS[activeThemeMode],
          // ↓ NEW: inject canonical + all template CSS variables
          ...buildTemplateCssVars(templateResolvedVariables),
        }}
      >
        {renderedNodes}
        {canRenderThemeToggle ? (
          <button
            type="button"
            aria-label={`Switch to ${activeThemeMode === "dark" ? "light" : "dark"} theme`}
            onClick={() => setThemeOverride(activeThemeMode === "dark" ? "light" : "dark")}
            className={clsx(
              themeTogglePositionClasses,
              "z-[var(--z-dialog)] inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/60 bg-background/92 text-foreground shadow-[0_10px_32px_rgba(15,23,32,0.18)] backdrop-blur sm:h-11 sm:w-11",
            )}
          >
            {activeThemeMode === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        ) : null}
        { hasModal ? itemModalNode && openModalId ? renderModal : (
          <DefaultModal
            open={!!openModalId}
            onClose={() => setOpenModalId(null)}
            item={modalItem}
            category={modalCategory}
          />
        ): null}
      </div>
    </TemplateThemeContext.Provider>
  );
}

export default TemplateRenderer; 
